import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { PushNotifications } from '@capacitor/push-notifications';
import { Purchases } from '@revenuecat/purchases-capacitor';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { SavePassword } from 'capacitor-ios-autofill-save-password';
import useSecretStorage from 'hooks/useSecretStorage';
import Keys from 'preferences/preferences';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiManager } from 'services/api/ApiManager';
import ApiTokenManager from 'services/api/ApiTokenManager';
import { identifyUser, resetIdentity, waitForFlagsForCurrentUser } from 'services/PostHogIdentity';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { AUTH_ROUTES, type AuthLocationState, getRedirectPath, routes } from 'services/RouteService';
import WebSocketService from 'services/WebSocketService';
import { toast } from 'sonner';
import resetAll from 'store/shared/resetAll';
import { userActions } from 'store/users';
import { selectCurrentUser } from 'store/users/selectors';
import Platform from 'utils/Platform';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

// Backoff between attempts at identifying the RevenueCat customer. Its length
// is the number of retries; a network blip resolves on the first one, and
// anything still failing after the last is reported to Sentry rather than
// retried forever.
const REVENUECAT_RETRY_DELAYS_MS = [1000, 3000, 8000];

type AuthContextValue = {
  status: AuthStatus;
  accessToken?: string;
  refreshToken?: string;
  user: any; // simplified typing with existing selectors
  tryToLoginFromStoredTokens: () => void;
  login: (params: { email: string; password: string }) => Promise<void>;
  logout: () => void;
  registerPreLogoutCallback: (cb: () => void | Promise<void>) => () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Detach this device from the account on the backend, otherwise it keeps
// receiving that account's push notifications after logging out: unregistering
// from the OS alone doesn't stop the server from sending to the token. Must run
// while the user's access token is still valid.
const unregisterPushToken = async () => {
  const { value: token } = await Preferences.get({ key: Keys.PUSH_REGISTERED_FCM_TOKEN });
  if (!token) return;
  await ApiUsersManager.unregisterFCMToken({ token });
  await Preferences.remove({ key: Keys.PUSH_REGISTERED_FCM_TOKEN });
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useSelector(selectCurrentUser);
  const {
    value: storedTokens,
    set: setStoredTokens,
    loading: storageLoading,
  } = useSecretStorage('gynger-access-tokens');
  const localStorageAccessToken = storedTokens?.access;
  const localStorageRefreshToken = storedTokens?.refresh;
  // we can't use the object directly because two different objects with the same values are not equal,
  // causing useEffects to trigger indefinitely, so we package the two tokens in a string to use as a dependency
  const storedTokensDependency = localStorageAccessToken + localStorageRefreshToken;

  // Stay in 'loading' until we know whether stored tokens exist, to prevent
  // a premature redirect to /welcome before the async storage read completes.
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [accessToken, setAccessToken] = useState<string | undefined>(undefined);
  const [refreshToken, setRefreshToken] = useState<string | undefined>(undefined);
  const fromLogoutRef = useRef(false);
  // Prevents re-entrant calls to tryToLoginFromStoredTokens while a getUsersMe
  // request is already in flight. Without this guard, each setTokens() call
  // (inside tryToLoginFromStoredTokens) writes back to SecureStorage, which
  // causes useSecretStorage to re-run its get(), briefly toggling storageLoading
  // false→true→false and re-triggering the bootstrap effect while status is
  // still 'loading' — producing an unbounded loop of /api/auth/users/me calls.
  const bootstrapInProgress = useRef(false);

  const preLogoutCallbacks = useRef<Set<() => void | Promise<void>>>(new Set());

  const registerPreLogoutCallback = useCallback((cb: () => void | Promise<void>) => {
    preLogoutCallbacks.current.add(cb);
    return () => preLogoutCallbacks.current.delete(cb);
  }, []);

  const queryClient = useQueryClient();

  // refresh queue state - prevents multiple simultaneous token refresh attempts
  // when multiple API requests fail with 401 errors at the same time
  const isRefreshing = useRef<boolean>(false);
  const refreshPromise = useRef<Promise<string> | null>(null);
  const requestsWaitingForRefresh = useRef<Array<(token: string) => void>>([]);

  const setTokens = useCallback(
    (params: { access?: string; refresh?: string }) => {
      setAccessToken(params.access);
      setRefreshToken(params.refresh);
      ApiManager.setTokens({ accessToken: params.access, refreshToken: params.refresh });
      setStoredTokens({ access: params.access, refresh: params.refresh });
    },
    [setStoredTokens],
  );

  const clearTokens = useCallback(() => {
    setAccessToken(undefined);
    setRefreshToken(undefined);
    ApiManager.setTokens({ accessToken: undefined, refreshToken: undefined });
    setStoredTokens({ access: undefined, refresh: undefined });
  }, [setStoredTokens]);

  const reRunWaitingRequestsWithNewToken = useCallback((newAccess: string) => {
    requestsWaitingForRefresh.current.forEach(cb => cb(newAccess));
    requestsWaitingForRefresh.current = [];
  }, []);

  const markRequestAsWaitingForRefresh = useCallback((cb: (token: string) => void) => {
    requestsWaitingForRefresh.current.push(cb);
  }, []);

  const performRefresh = useCallback(async (): Promise<string> => {
    if (isRefreshing.current && refreshPromise.current) {
      return refreshPromise.current;
    }
    isRefreshing.current = true;

    const tokens = ApiManager.getTokens();
    const refresh = tokens.refreshToken;
    if (!refresh) {
      isRefreshing.current = false;
      refreshPromise.current = null;
      throw new Error('No refresh token');
    }

    const p = ApiTokenManager.refreshAccessToken({ refresh })
      .then(result => {
        setTokens({ access: result.access, refresh: result.refresh });
        reRunWaitingRequestsWithNewToken(result.access);
        isRefreshing.current = false;
        refreshPromise.current = null;
        return result.access;
      })
      .catch(err => {
        isRefreshing.current = false;
        refreshPromise.current = null;
        requestsWaitingForRefresh.current = [];
        throw err;
      });

    refreshPromise.current = p;
    return p;
  }, [reRunWaitingRequestsWithNewToken, setTokens]);

  const logout = useCallback(
    async (errorOccurred: boolean = false) => {
      WebSocketService.close();
      setStatus(errorOccurred ? 'error' : 'unauthenticated');

      if (!errorOccurred) {
        fromLogoutRef.current = true;
        await Promise.allSettled([
          ...[...preLogoutCallbacks.current].map(cb => Promise.resolve(cb())),
          unregisterPushToken(),
        ]);
        clearTokens();
      }

      resetIdentity();
      Sentry.setUser(null);
      dispatch(resetAll());
      queryClient.clear();

      Preferences.remove({ key: Keys.MESSAGE_DRAFTS }).catch(error => {
        console.error('Failed to clear message drafts on logout:', error);
      });
      // Unsent messages survive the logout a network error causes, to be sent once back
      if (!errorOccurred) {
        Preferences.remove({ key: Keys.FAILED_MESSAGES }).catch(error => {
          console.error('Failed to clear unsent messages on logout:', error);
        });
      }

      if (Platform.isNative()) {
        Purchases.logOut();
      }

      if (Capacitor.isPluginAvailable('PushNotifications')) {
        void PushNotifications.unregister();
      }
    },
    [clearTokens, dispatch],
  );

  // Single place for auth-related redirects: unauthenticated/error → Welcome (only when on a protected route).
  // Starting status as 'loading' (see useState initializer above) is sufficient to prevent a premature
  // redirect while the async storage read is in flight — this effect only fires once status
  // has resolved to 'unauthenticated' or 'error', at which point storageLoading is already false.
  useEffect(() => {
    if (status !== 'unauthenticated' && status !== 'error') return;
    const isAuthPage = AUTH_ROUTES.some(r => {
      const prefix = r.split('/:')[0];
      return location.pathname === prefix || location.pathname.startsWith(prefix + '/');
    });
    if (isAuthPage) return;
    const state = fromLogoutRef.current
      ? ({ fromLogout: true } as AuthLocationState)
      : ({ from: { pathname: location.pathname, search: location.search } } as AuthLocationState);
    if (fromLogoutRef.current) fromLogoutRef.current = false;
    navigate(routes.welcome.route, { state, replace: true });
  }, [location.pathname, location.search, navigate, status]);

  // Post-login redirect: when authenticated on Welcome/Login, go to saved URL or home
  useEffect(() => {
    if (status !== 'authenticated') return;
    const isAuthPage = location.pathname === routes.welcome.route || location.pathname === routes.login.route;
    if (!isAuthPage) return;
    const fromLogout = (location.state as AuthLocationState | undefined)?.fromLogout;
    const redirect = fromLogout ? undefined : getRedirectPath(location);
    navigate(redirect ?? routes.home.route, { replace: true });
  }, [location, navigate, status]);

  // install interceptors once
  useEffect(() => {
    const axios = ApiManager.getInstance();

    const reqId = axios.interceptors.request.use(async config => {
      const token = ApiManager.getAccessToken();
      if (token) {
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` } as any;
      }
      return config;
    });

    const resId = axios.interceptors.response.use(
      response => response,
      async error => {
        const original = error.config;
        const response = error.response;
        if (!original || original._retry) {
          toast.error(t('loginRegister.errors.login'));
          error._reportToSentry = true;
          return Promise.reject(error);
        }

        const responseObject = response?.data;
        if (original.url === '/api/auth/jwt/refresh/') {
          if (responseObject?.code === 'token_not_valid') {
            // refresh token expired
            toast.error(t('loginRegister.errors.disconnected'));
            return Promise.reject(error);
          } else {
            // unknown refresh error
            toast.error(t('loginRegister.errors.login'));
            return Promise.reject(error);
          }
        }

        if (error?.response?.status === 401) {
          if (original.url === '/api/auth/jwt/create/') {
            // wrong login attempt
            toast.error(t('loginRegister.errors.unauthorized'));
            return Promise.reject(error);
          }

          // any other 401 error : we try to refresh and retry the request
          original._retry = true;

          try {
            if (isRefreshing.current) {
              const newToken = await new Promise<string>(resolve => markRequestAsWaitingForRefresh(resolve));
              original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
              return axios(original);
            }

            const newToken = await performRefresh();
            original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
            return axios(original);
          } catch (e: any) {
            const url = e?.config?.url ?? e?.request?.url;
            const status = e?.response?.status;
            const refreshFailed = url === '/api/auth/jwt/refresh/' && status !== 401;
            const requestFailedBecauseOfNetworkError = !e?.response?.data?.code;
            const should_mark_network_error = refreshFailed || requestFailedBecauseOfNetworkError;
            logout(should_mark_network_error);
            return Promise.reject(e);
          }
        }

        // if it's any network error, logout with an error
        if (!error.response?.status) {
          logout(true);
        }

        return Promise.reject(error);
      },
    );

    return () => {
      const ax = ApiManager.getInstance();
      ax.interceptors.request.eject(reqId);
      ax.interceptors.response.eject(resId);
    };
  }, [clearTokens, dispatch, markRequestAsWaitingForRefresh, performRefresh]);

  const login = useCallback(
    async (params: { email: string; password: string }) => {
      setStatus('loading');
      try {
        const result = await ApiTokenManager.getAccessToken(params);
        setTokens({ access: result.access, refresh: result.refresh });
        const me = await ApiUsersManager.getUsersMe();
        identifyUser(me);
        dispatch(userActions.setCurrentUser(me));
        Sentry.setUser({ id: me.id });
        // Identifying invalidates the cached feature flags: until the reload
        // lands they still hold the values evaluated for the anonymous device id.
        // Everything downstream keys off this status — the post-login redirect,
        // Shell and its flag-reading effects, the onboarding variant, and
        // `registration.completed` captured by whoever awaited us — so stay in
        // 'loading' until the flags belong to this user. Never rejects; falls
        // open a few seconds in.
        await waitForFlagsForCurrentUser();
        setStatus('authenticated');
        if (Platform.getPlatform() === 'ios') {
          SavePassword.promptDialog({ username: params.email, password: params.password });
        }
      } catch (e) {
        clearTokens();
        setStatus('unauthenticated');
        throw e;
      }
    },
    [clearTokens, dispatch, setTokens],
  );

  const tryToLoginFromStoredTokens = useCallback(() => {
    const access = storedTokens?.access;
    const refresh = storedTokens?.refresh;

    if (access && refresh && (status === 'unauthenticated' || status === 'error' || status === 'loading')) {
      if (bootstrapInProgress.current) return;
      bootstrapInProgress.current = true;
      setTokens({ access, refresh });
      setStatus('loading');

      ApiUsersManager.getUsersMe()
        .then(async me => {
          identifyUser(me);
          dispatch(userActions.setCurrentUser(me));
          Sentry.setUser({ id: me.id });
          // Same as in login(): a cold start for an already-identified user
          // resolves immediately, since posthog only reloads on an id change.
          await waitForFlagsForCurrentUser();
          setStatus('authenticated');
        })
        .catch(err => {
          // any error that is not a 401 or a network error; therefore probably a logic error
          console.error('Unexpected error while logging in', err);
          setStatus('error');
        })
        .finally(() => {
          bootstrapInProgress.current = false;
        });
    } else if (!access || !refresh) {
      setStatus('unauthenticated');
    }
  }, [clearTokens, dispatch, setTokens, status, storedTokensDependency]);

  // bootstrap from tokens once storage has been read
  useEffect(() => {
    if (storageLoading) return;
    tryToLoginFromStoredTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedTokensDependency, storageLoading]);

  // Identify the RevenueCat customer and put their subscription in the store.
  //
  // This is the only place that identifies the SDK, and it is what everything
  // reading `currentUserSubscription` depends on: the paywall's early grant
  // between a purchase and the webhook, and the subscription line on the
  // settings page.
  //
  // It used to be a bare `.then()` chained into a second `getCustomerInfo()`
  // call. Either call rejecting (RevenueCat unreachable, a slow network, a
  // StoreKit hiccup) was swallowed silently, nothing retried, and because
  // `currentUser.id` does not change again for the life of the app process the
  // store kept a subscription-less customer until the app was force-quit.
  // `logIn` already returns the identified customer's info, so there is no
  // second call left to fail; what does fail is retried and, if it keeps
  // failing, reported to Sentry — the absence of which is why this was
  // invisible for so long.
  useEffect(() => {
    if (!Platform.isNative() || !currentUser?.id) return;

    const appUserID = String(currentUser.id);
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const identify = async (attempt: number) => {
      try {
        const { customerInfo } = await Purchases.logIn({ appUserID });
        if (cancelled) return;
        dispatch(userActions.setCurrentUserSubscription(customerInfo));
      } catch (error) {
        if (cancelled) return;
        if (attempt < REVENUECAT_RETRY_DELAYS_MS.length) {
          retryTimer = setTimeout(() => identify(attempt + 1), REVENUECAT_RETRY_DELAYS_MS[attempt]);
          return;
        }
        Sentry.captureException(error, { tags: { revenuecat_stage: 'logIn' } });
      }
    };

    identify(0);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [currentUser?.id, dispatch]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      accessToken,
      refreshToken,
      user: currentUser,
      login,
      logout,
      tryToLoginFromStoredTokens,
      registerPreLogoutCallback,
    }),
    [accessToken, currentUser, login, logout, refreshToken, status, registerPreLogoutCallback],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
